import { CNCMonitorMachineStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCMonitorMachine/CNCMonitorMachineStepDatabase';
import { CNCOperateAutodoorStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCOperateAutodoor/CNCOperateAutodoorStepDatabase';
import { CNCOperateWorkholdingStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCOperateWorkholding/CNCOperateWorkholdingStepDatabase';
import { CNCRunProgramStepDatabase } from '@sb/integrations/implementations/CNCMachine/step-wizard/CNCRunProgram/CNCRunProgramStepDatabase';
import { MoveVerticalLiftStepDatabase } from '@sb/integrations/implementations/dynamic-base/step-wizard/MoveVerticalLiftStepDatabase';
import { ZeroForceTorqueSensorStepDatabase } from '@sb/integrations/implementations/force-torque-general/step-wizard/ZeroForceTorqueSensorStepDatabase';
import { ActuateGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/ActuateGripperStepDatabase';
import { DisableGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/DisableGripperStepDatabase';
import { EnableGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/EnableGripperStepDatabase';
import { HaasControlRegionStepDatabase } from '@sb/integrations/implementations/HaasMill/frontend/HaasControlRegionStepDatabase';
import { HaasRunProgramStepDatabase } from '@sb/integrations/implementations/HaasMill/frontend/HaasRunProgramStepDatabase';
import { ActuateScrewdriverStepDatabase } from '@sb/integrations/implementations/onRobot/OnRobotScrewdriver/step-wizard/ActuateScrewdriverStepDatabase';
import { ActuateVacuumStepDatabase } from '@sb/integrations/implementations/onRobot/OnRobotVGP20/step-wizard/ActuateVacuumStepDatabase';
import { RotaryWeldStepDatabase } from '@sb/integrations/implementations/WeldMachine/shared/step-wizard/RotaryWeld/RotaryWeldStepDatabase';
import { WeldStepDatabase } from '@sb/integrations/implementations/WeldMachine/shared/step-wizard/Weld/WeldStepDatabase';
import type { EquipmentItem } from '@sb/integrations/types';
// Step namespaces are imported from their files directly, NOT via the
// `@sb/remote-control/step` barrel. This module sits in an import cycle
// (step modules -> blockConnectorValidation -> this file); going through the
// barrel adds an `export *` re-export hop that can capture a mid-cycle
// `undefined` for namespaces whose module hasn't finished evaluating, breaking
// binding liveness. Direct imports read each module's own live exports —
// the same pattern stepConfigurationByKind.ts uses.
import { AddOffsetStep } from '@sb/remote-control/step/addOffset';
import { AnticipatePayloadStep } from '@sb/remote-control/step/anticipatePayload';
import { AntigravityStep } from '@sb/remote-control/step/antigravity';
import { ArcStep } from '@sb/remote-control/step/arc';
import { CallSubroutineStep } from '@sb/remote-control/step/callSubroutine';
import { CaptureStep } from '@sb/remote-control/step/capture';
import { ClassifyStep } from '@sb/remote-control/step/classify';
import { CodeBlockStep } from '@sb/remote-control/step/codeBlock';
import { CreateDemonstrationStep } from '@sb/remote-control/step/createDemonstration';
import { CreateRecordingStep } from '@sb/remote-control/step/createRecording';
import { ElseStep } from '@sb/remote-control/step/else';
import { ElseIfStep } from '@sb/remote-control/step/elseIf';
import { ExternalControlStep } from '@sb/remote-control/step/externalControl';
import { IfStep } from '@sb/remote-control/step/if';
import { LocateStep } from '@sb/remote-control/step/locate';
import { LoopStep } from '@sb/remote-control/step/loop';
import { LoopControlStep } from '@sb/remote-control/step/loopControl';
import { MiraiRunSkillStep } from '@sb/remote-control/step/miraiRunSkill';
import { MoveArmToTargetStep } from '@sb/remote-control/step/moveArmTo';
import { MoveArmToTargetStepV2 } from '@sb/remote-control/step/moveArmToV2';
import { NetworkRequestStep } from '@sb/remote-control/step/networkRequest';
import { OperateEquipmentStep } from '@sb/remote-control/step/operateEquipment';
import { PressButtonStep } from '@sb/remote-control/step/pressButton';
import { PushModeStep } from '@sb/remote-control/step/pushMode';
import { RunDemonstrationStep } from '@sb/remote-control/step/runDemonstration';
import { RunInBackgroundStep } from '@sb/remote-control/step/runInBackground';
import { RunInspectSkillStep } from '@sb/remote-control/step/runInspectSkill';
import { RunLocateSkillStep } from '@sb/remote-control/step/runLocateSkill';
import { RunSkillStep } from '@sb/remote-control/step/runSkill';
import { SamplePositioningApproachStep } from '@sb/remote-control/step/samplePositioningApproach';
import { SetActiveTooltipStep } from '@sb/remote-control/step/setActiveTooltip';
import { SetEnvironmentVariableStep } from '@sb/remote-control/step/setEnvironmentVariable';
import { SetIOStep } from '@sb/remote-control/step/setIO';
import { SuppressedStep } from '@sb/remote-control/step/suppressed';
import { TeleopStep } from '@sb/remote-control/step/teleop';
import { TriggerFaultStep } from '@sb/remote-control/step/triggerFault';
import { UpdateVisualizationStep } from '@sb/remote-control/step/updateVisualization';
import { WaitStep } from '@sb/remote-control/step/wait';
import { WaitForConfirmationStep } from '@sb/remote-control/step/waitForConfirmation';
import { WaypointStep } from '@sb/remote-control/step/waypoint';
import { Step } from '@sb/remote-control/types';
import type { StepKind } from '@sb/routine-runner';

// Built lazily rather than as a module-level literal. This module sits in an
// import cycle (step modules -> blockConnectorValidation -> this file): a
// module-level literal can evaluate while step modules are still
// mid-initialization and permanently capture `undefined` for any namespace
// that hasn't run yet — getStepKindInfo would then return the "(unknown)"
// fallback for real kinds. Deferring the build to the first call (normally
// after the import graph has settled) reads the bindings' final values.
const buildStepKindInfoMap = (): Record<StepKind, Step.StepKindInfo> => ({
  ActuateGripper: ActuateGripperStepDatabase,
  ActuateScrewdriver: ActuateScrewdriverStepDatabase,
  ActuateVacuum: ActuateVacuumStepDatabase,
  AddOffset: AddOffsetStep,
  Antigravity: AntigravityStep,
  Arc: ArcStep,
  AnticipatePayload: AnticipatePayloadStep,
  CallSubroutine: CallSubroutineStep,
  Capture: CaptureStep,
  Classify: ClassifyStep,
  CodeBlock: CodeBlockStep,
  CreateRecording: CreateRecordingStep,
  CreateDemonstration: CreateDemonstrationStep,
  CNCRunProgram: CNCRunProgramStepDatabase,
  CNCMonitorMachine: CNCMonitorMachineStepDatabase,
  CNCOperateAutodoor: CNCOperateAutodoorStepDatabase,
  CNCOperateWorkholding: CNCOperateWorkholdingStepDatabase,
  DisableGripper: DisableGripperStepDatabase,
  Else: ElseStep,
  ElseIf: ElseIfStep,
  EnableGripper: EnableGripperStepDatabase,
  ExternalControl: ExternalControlStep,
  HaasControlRegion: HaasControlRegionStepDatabase,
  HaasRunProgram: HaasRunProgramStepDatabase,
  If: IfStep,
  Locate: LocateStep,
  Loop: LoopStep,
  LoopControl: LoopControlStep,
  MiraiRunSkill: MiraiRunSkillStep,
  MoveArmTo: MoveArmToTargetStep,
  MoveArmToV2: MoveArmToTargetStepV2,
  MoveVerticalLift: MoveVerticalLiftStepDatabase,
  NetworkRequest: NetworkRequestStep,
  OperateEquipment: OperateEquipmentStep,
  PressButton: PressButtonStep,
  PushMode: PushModeStep,
  RunInBackground: RunInBackgroundStep,
  RunInspectSkill: RunInspectSkillStep,
  RunLocateSkill: RunLocateSkillStep,
  RunLearnedModel: RunSkillStep,
  RunDemonstration: RunDemonstrationStep,
  SamplePositioningApproach: SamplePositioningApproachStep,
  SetActiveTooltip: SetActiveTooltipStep,
  SetEnvironmentVariable: SetEnvironmentVariableStep,
  SetIO: SetIOStep,
  Suppressed: SuppressedStep,
  Teleop: TeleopStep,
  Wait: WaitStep,
  WaitForConfirmation: WaitForConfirmationStep,
  Waypoint: WaypointStep,
  RotaryWeld: RotaryWeldStepDatabase,
  Weld: WeldStepDatabase,
  TriggerFault: TriggerFaultStep,
  UpdateVisualization: UpdateVisualizationStep,
  ZeroForceTorqueSensor: ZeroForceTorqueSensorStepDatabase,
});

let stepKindInfoMap: Record<StepKind, Step.StepKindInfo> | null = null;

const getStepKindInfoMap = (): Record<StepKind, Step.StepKindInfo> => {
  if (stepKindInfoMap) return stepKindInfoMap;

  const built = buildStepKindInfoMap();

  // Only memoize a fully-resolved build. A call that lands mid-import-cycle
  // can still observe a not-yet-evaluated step namespace as `undefined`;
  // caching that build would poison every later caller, so rebuild until all
  // entries are present.
  if (Object.values(built).every((info) => info !== undefined)) {
    stepKindInfoMap = built;
  }

  return built;
};

export function getStepKindInfo(stepKind: StepKind): Step.StepKindInfo {
  return (
    getStepKindInfoMap()[stepKind] ?? {
      // default if step kind doesn't exist (e.g. after downgrade)
      name: `${stepKind} (unknown)`,
      description: `Unrecognized step kind`,
      librarySection: 'Basic',
    }
  );
}

type StepKindLibrary = {
  title: Step.LibrarySection;
  stepKinds: StepKind[];
}[];

let stepKindLibraryCache: StepKindLibrary | null = null;

export const getStepKindLibrary = (): StepKindLibrary => {
  if (stepKindLibraryCache) return stepKindLibraryCache;

  const libraryStepKinds = Object.entries(getStepKindInfoMap())
    // A mid-cycle build can hold `undefined` entries (see getStepKindInfoMap);
    // skip them rather than destructure-crashing on the transient build.
    .filter((entry): entry is [string, Step.StepKindInfo] => entry[1] != null)
    .map(([stepKind, { librarySection, librarySort, name }]) => {
      return {
        stepKind: stepKind as StepKind,
        librarySection,
        sort: `${librarySort ?? 'z'} ${name}`,
      };
    })
    .sort((a, b) => a.sort.localeCompare(b.sort));

  const built = Object.values(Step.LibrarySection).map((title) => ({
    title,
    stepKinds: libraryStepKinds
      .filter(({ librarySection }) => librarySection === title)
      .map(({ stepKind }) => stepKind),
  }));

  // Cache only once the underlying map build was complete — a permanently
  // memoized mid-cycle build would silently drop step kinds from the library
  // forever, even after getStepKindInfoMap self-corrects.
  if (stepKindInfoMap !== null) {
    stepKindLibraryCache = built;
  }

  return built;
};

export function isEquipmentRequiredForStepKind(
  stepKind: StepKind,
  equipment: EquipmentItem[],
): boolean {
  const stepDeviceKinds = getStepKindInfo(stepKind).deviceKinds;

  if (!stepDeviceKinds) {
    return false;
  }

  return !equipment.some((device) =>
    stepDeviceKinds.includes(device.config.kind),
  );
}
